'use client'

import { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/navigation'

import Typography from '@mui/material/Typography'

import classnames from 'classnames'
import { getSession, signIn, useSession } from 'next-auth/react'

import axios from 'axios'

import { OTPInput } from 'input-otp'

import type { SlotProps } from 'input-otp'

import FingerprintJS from '@fingerprintjs/fingerprintjs'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'

import showToastAlert from '@components/Toast'
import { useSettings } from '@core/hooks/useSettings'
import { encryptAES } from '@/utils/getUrlApiLogin'
import LogoLogin from '@/components/layout/shared/LogoLogin'
import CustomButton from '@/components/layout/shared/CustomButton'
import themeConfig from '@configs/themeConfig'
import styles from './inputOtp.module.css'

interface DataUnit {
  ID_UNIT: string
  NAMA_UNIT: string
  ALAMAT: string
  KOTA: string
  LABA: string
  NONRACIK: string
  RACIK: string
  DT: string
  BPJS_LABA: string
  BPJS_NONRACIK: string
  BPJS_RACIK: string
  BPJS_DT: string
  ID_AKUN: string
  ID_KAS: string
  ID_JUAL: string
}

type Pelanggan = {
  ID_USAHA: string
  NAMA_USAHA: string
  ALAMAT_USAHA: string
  KOTA_USAHA: string
  NPWP: string
  URLPUSAT: string
  URLUTAMA: string
  URLPUSAT_DOMAIN: string
  URLUTAMA_DOMAIN: string
  GAMBAR_LOGO: string
  GAMBAR_BACKGROUND: string
}

const Slot = (props: SlotProps & { disabled?: boolean }) => {
  return (
    <div
      className={classnames(
        styles.slot,
        { [styles.slotActive]: props.isActive },
        { [styles.slotDisabled]: props.disabled }
      )}
    >
      {props.char !== null && <div>{props.char}</div>}
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  )
}

const FakeCaret = () => {
  return (
    <div className={styles.fakeCaret}>
      <div className='w-px h-5 bg-textPrimary' />
    </div>
  )
}

const Login = () => {
  const { settings } = useSettings()

  const [idUsaha, setIdUsaha] = useState('')
  const [openUnit, setOpenUnit] = useState(false)

  const router = useRouter()
  const { data: session, status } = useSession()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [unitList, setUnitList] = useState<DataUnit[]>([]) // Tambahkan state untuk unit list
  const [dataPelanggan, setDataPelanggan] = useState<Pelanggan | null>(null)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [namaUsaha, setNamaUsaha] = useState('')
  const unitRef = useRef<HTMLInputElement | null>(null)
  const [selectedUnit, setUnitSelected] = useState('')
  const [rowSelectedUnit, setRowSelectedUnit] = useState<DataUnit | null>(null)
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [fingerprintId, setFingerprintId] = useState<string>('')
  const [openFingerprintDialog, setOpenFingerprintDialog] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    // Initialize an agent
    const fpPromise = FingerprintJS.load()

    fpPromise
      .then(fp => fp.get())
      .then(result => {
        setFingerprintId(result.visitorId)
      })

    getLocation()
  }, [])

  const getLocation = () => {
    if (!navigator.geolocation) {
      showToastAlert('warning', 'Geolocation tidak didukung browser ini.')

      return
    }

    // Opsi konfigurasi untuk akurasi tinggi
    const options = {
      enableHighAccuracy: true, // Memaksa browser menggunakan GPS (jika tersedia)
      timeout: 10000, // Waktu tunggu maksimal (10 detik) sebelum error
      maximumAge: 0 // Jangan gunakan lokasi yang dicache, cari yang baru
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        setLatitude(position.coords.latitude)
        setLongitude(position.coords.longitude)
      },
      error => {
        showToastAlert('warning', 'Gagal mendapatkan lokasi: ' + error.message)
      },
      options // <--- Masukkan opsi di sini sebagai parameter ketiga
    )
  }

  useEffect(() => {
    if (status === 'authenticated') {
      const redirectTo = '/harga-home'

      router.push(redirectTo)
    }
  }, [status, session, router])

  const clearAllCookies = async () => {
    // Hapus client-side cookies
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    // Hapus server-side cookies
    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }

  const doLoginLangganan = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!idUsaha) {
      showToastAlert('info', 'ID Usaha tidak boleh kosong')

      return
    }

    // const url = await getUrlApiLogin()

    setLoading(true)
    await clearAllCookies()

    try {
      const formData = {
        idusaha: idUsaha
      }

      const encryptedData = await encryptAES(JSON.stringify(formData))

      const response = await axios.post(
        (process.env.NEXT_PUBLIC_API_URL_LOGIN_FRANCHISE as string) + 'login/loginfranchise',
        {
          data: encryptedData
        }
      )

      if (response.data.metadata.code === 200) {
        setDataPelanggan(response.data.response.list[0] || {})
        setNamaUsaha(response.data.response.list[0].NAMA_USAHA || '')

        // Ambil list unit
        const unitResponse = await axios.post(response.data.response.list[0].URLUTAMA_DOMAIN + 'ref/unitusaha', {
          data: encryptedData
        })

        if (unitResponse.data.metadata.code === 200) {
          const units = unitResponse.data.response.list || []

          setUnitList(units)

          if (units.length > 0) {
            setUnitSelected(units[0].ID_UNIT)
            setRowSelectedUnit(units[0])
          }

          setTimeout(() => {
            unitRef.current?.focus()
          }, 100)
        } else {
          setUnitList([])
          setUnitSelected('')
          setRowSelectedUnit(null)
        }

        setOpenUnit(true)
      } else {
        showToastAlert('info', response.data.metadata.message)
      }
    } catch (error: any) {
      console.error('Error login:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (username === '' || password === '') {
      showToastAlert('info', 'Username dan Password tidak boleh kosong')

      return
    }

    setLoading(true)
    await clearAllCookies()

    // const url = await getUrlApiLogin()

    // let isurlpublic = ''

    // if (url === dataPelanggan?.URLUTAMA_DOMAIN) {
    //   isurlpublic = 'true'
    // } else {
    //   isurlpublic = 'false'
    // }

    const formData = {
      user: username,
      pass: password,
      idunit: selectedUnit,
      deviceid: fingerprintId
    }

    const encryptedData = await encryptAES(JSON.stringify(formData))

    try {
      const result = await signIn('credentials', {
        redirect: false,
        data: encryptedData,
        isurlpublic: 'true',
        url: dataPelanggan?.URLUTAMA,
        dataUnit: JSON.stringify(rowSelectedUnit),
        dataPelanggan: JSON.stringify(dataPelanggan)
      })

      if (result?.error) {
        showToastAlert('info', result.error)
      } else {
        try {
          const session = await getSession()
          const idPegawai = await session?.user?.idPegawai

          const logData = {
            idunit: rowSelectedUnit?.ID_UNIT,
            idusaha: dataPelanggan?.ID_USAHA,
            idpegawai: idPegawai,
            status: 0, // login
            namauser: username,
            longitude: longitude || '',
            latitude: latitude || ''
          }

          const encryptedLogData = await encryptAES(JSON.stringify(logData))

          const logResponse = await axios.post((dataPelanggan?.URLUTAMA_DOMAIN as string) + 'login/savelogin_logout', {
            data: encryptedLogData
          })

          if (logResponse.data?.metadata?.code !== 200) {
            showToastAlert('error', logResponse.data?.metadata?.message || 'Gagal menyimpan log login')
            setLoading(false)

            return
          } else {
            router.push(themeConfig.homePageUrl)
          }
        } catch (err) {
          showToastAlert('error', 'Gagal menyimpan log login')
          setLoading(false)

          return
        }
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseUnit = () => {
    setOpenUnit(false)
    setDataPelanggan(null)
    setUnitList([])
  }

  const handleChangeUnit = (value: any) => {
    if (value) {
      setUnitSelected(value.ID_UNIT)
      setRowSelectedUnit(value)
    } else {
      setUnitSelected('')
      setRowSelectedUnit(null)
    }
  }

  return (
    <>
      <div className='flex bs-full justify-center'>
        <div
          className={classnames(
            'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
            {
              'border-ie': settings.skin === 'bordered'
            }
          )}
        >
          <img
            src='/images/icon/logo_blue_big.jpg'
            alt=''
            className='absolute inset-0 w-full h-full object-cover z-[-1]'
          />
        </div>
        <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
          <div className='absolute block sm:hidden block-start-5 inline-start-6'>
            <LogoLogin />
          </div>
          <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
            <div>
              <Typography variant='h5' className='text-center'>
                Selamat datang di
                <strong>{` ${themeConfig.templateName}! 👋🏻`}</strong>
              </Typography>
            </div>

            {/* Tambahkan di sini */}
            {/* {locationError && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography color='error' variant='body2' sx={{ mb: 1 }}>
                  {locationError}
                </Typography>
                <CustomButton loading={false} type='button' color='warning' onClick={getLocation} size='small'>
                  Izinkan Lokasi
                </CustomButton>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                  Jika tombol tidak memunculkan permintaan izin, silakan aktifkan izin lokasi di pengaturan browser
                  Anda.
                </Typography>
              </Box>
            )} */}
            <>
              <form
                noValidate
                onSubmit={!openUnit ? doLoginLangganan : doLogin}
                autoComplete='off'
                className='flex flex-col gap-5'
              >
                <div className='flex flex-col gap-2'>
                  <Typography>Kode Franchise</Typography>
                  <OTPInput
                    onChange={value => {
                      if (openUnit) return
                      setIdUsaha(value)
                      setOpenUnit(false)
                    }}
                    value={idUsaha ?? ''}
                    maxLength={4}
                    disabled={openUnit}
                    containerClassName='flex items-center'
                    render={({ slots }) => (
                      <div className='flex items-center justify-between w-full gap-4'>
                        {slots.slice(0, 4).map((slot, idx) => (
                          <Slot key={idx} {...slot} disabled={openUnit} />
                        ))}
                      </div>
                    )}
                  />
                </div>

                {openUnit && (
                  <>
                    <TextField
                      autoFocus
                      fullWidth
                      label='Nama Usaha'
                      value={namaUsaha}
                      InputProps={{
                        readOnly: openUnit,
                        style: { textAlign: 'left', backgroundColor: openUnit ? '#f0f0f0' : '' },
                        inputProps: {
                          style: { textAlign: 'left', textTransform: 'uppercase' }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: openUnit ? '#f0f0f0' : ''
                        },
                        '& .MuiInput-input': {
                          textAlign: 'left'
                        }
                      }}
                    />
                    <Autocomplete
                      autoFocus
                      options={unitList}
                      getOptionLabel={(option: string | DataUnit) => {
                        if (typeof option === 'string') {
                          return option
                        } else {
                          return option.NAMA_UNIT
                        }
                      }}
                      value={
                        unitList.find((option: DataUnit) => Number(option.ID_UNIT) === Number(selectedUnit)) ||
                        undefined
                      }
                      onChange={(event, value) => {
                        handleChangeUnit(value as DataUnit)
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='Unit Usaha'
                          inputRef={input => {
                            unitRef.current = input
                          }}
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true // Membuat input tidak bisa diketik manual
                          }}
                        />
                      )}
                      disableClearable
                    />
                    <TextField autoFocus fullWidth label='Username' onChange={e => setUsername(e.target.value)} />
                    <TextField
                      fullWidth
                      label='Password'
                      type={isPasswordShown ? 'text' : 'password'}
                      onChange={e => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              size='small'
                              edge='end'
                              onClick={handleClickShowPassword}
                              onMouseDown={e => e.preventDefault()}
                            >
                              <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </>
                )}

                {/* Login Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    gap: 2
                  }}
                >
                  <CustomButton type='submit' loading={loading} color='primary'>
                    Log In
                  </CustomButton>
                  {openUnit && (
                    <CustomButton type='button' onClick={handleCloseUnit} loading={loading} color='secondary'>
                      Log Out
                    </CustomButton>
                  )}
                </Box>
                {/* <Divider>Tidak Bisa Login?</Divider>
                <CustomButton
                  loading={false}
                  fullWidth={true}
                  type='button'
                  color='info'
                  onClick={() => setOpenFingerprintDialog(true)}
                  size='small'
                >
                  Get Device ID
                </CustomButton> */}
              </form>
            </>
          </div>
        </div>
      </div>

      <Dialog
        open={openFingerprintDialog}
        onClose={() => setOpenFingerprintDialog(false)}
        maxWidth='xs'
        fullWidth
        transitionDuration={0}
      >
        <DialogContent className='overflow-visible pbs-0 sm:pbe-2 sm:pli-5'>
          <div className='flex justify-between items-center mb-5 mt-5'>
            <Typography variant='h5'>Fingerprint ID</Typography>
            <IconButton onClick={() => setOpenFingerprintDialog(false)} color='primary' size='small' tabIndex={-1}>
              <i className='ri-close-line' />
            </IconButton>
          </div>
          <Box sx={{ py: 2 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2, textAlign: 'center' }}>
              Silakan copy code ini dan kasihkan ke pihak IT
            </Typography>
            <Typography
              variant='body1'
              sx={{
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                mb: 2
              }}
            >
              {fingerprintId || 'Loading...'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CustomButton
                loading={false}
                type='button'
                color='primary'
                onClick={() => {
                  if (fingerprintId) {
                    navigator.clipboard.writeText(fingerprintId)
                    showToastAlert('success', 'Fingerprint ID berhasil disalin!')
                    setOpenFingerprintDialog(false)
                  }
                }}
                size='small'
              >
                Copy
              </CustomButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Login
